import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Select } from '@silverfalls/sterling';
import { FilterInput } from '../components/FilterInput.js';
import { formatRelativeDate } from '../lib/format.js';
import { sortBy, toggleSort, type SortState } from '../lib/sort.js';
import {
  fetchReceipts,
  type ReceiptKind,
  type ReceiptRow,
} from '../api/p2p.js';

// Sterling density: receipts list is a stream-of-events view — lead with
// "when", then "what", "against what", "who". receiptRef drops (structural).
type ReceiptSortKey =
  | 'receivedAt'
  | 'kind'
  | 'poRef'
  | 'receivedBy'
  | 'lines'
  | 'attachments';

function receiptSortAccessor(r: ReceiptRow, key: ReceiptSortKey): unknown {
  switch (key) {
    case 'receivedAt':
      return r.receivedAt;
    case 'kind':
      return r.kind;
    case 'poRef':
      return r.poRef;
    case 'receivedBy':
      return r.receivedBy;
    case 'lines':
      return r.lines.length;
    case 'attachments':
      return r.attachments.length;
  }
}

function matchesQuery(r: ReceiptRow, q: string): boolean {
  if (q === '') return true;
  const needle = q.toLowerCase();
  return (
    r.receiptRef.toLowerCase().includes(needle) ||
    r.poRef.toLowerCase().includes(needle) ||
    r.receivedBy.toLowerCase().includes(needle) ||
    r.kind.toLowerCase().includes(needle) ||
    (r.notes?.toLowerCase().includes(needle) ?? false)
  );
}

const KIND_FILTERS: ReadonlyArray<{ label: string; value: ReceiptKind | 'ALL' }> = [
  { label: 'All', value: 'ALL' },
  { label: 'Goods', value: 'GOODS' },
  { label: 'Service', value: 'SERVICE' },
];

interface ReceiptsProps {
  readonly opcoRef: string;
}

export function Receipts({ opcoRef }: ReceiptsProps): JSX.Element {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ReadonlyArray<ReceiptRow>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [kindFilter, setKindFilter] = useState<ReceiptKind | 'ALL'>('ALL');
  const [sort, setSort] = useState<SortState<ReceiptSortKey> | null>({
    key: 'receivedAt',
    direction: 'desc',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await fetchReceipts(opcoRef);
      setRows(r);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [opcoRef]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    let r = rows.filter((row) => matchesQuery(row, query));
    if (kindFilter !== 'ALL') {
      r = r.filter((row) => row.kind === kindFilter);
    }
    return sortBy(r, sort, receiptSortAccessor);
  }, [rows, query, kindFilter, sort]);

  const onSort = (key: ReceiptSortKey) => setSort((prev) => toggleSort(prev, key));

  const serviceCount = rows.filter((r) => r.kind === 'SERVICE').length;
  const goodsCount = rows.length - serviceCount;

  return (
    <section className="vendors-screen">
      <div className="screen-header">
        <div>
          <h2>Receipts</h2>
          <p className="screen-header-desc">
            {rows.length} {rows.length === 1 ? 'receipt' : 'receipts'}
            {goodsCount > 0 && ` · ${goodsCount} goods`}
            {serviceCount > 0 && ` · ${serviceCount} service`}
          </p>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-neg" role="alert">
          Couldn't load receipts: {loadError}
        </div>
      )}

      {loading ? (
        <div className="t-body-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>
            No receipts in {opcoRef} yet. Receipts are recorded from a PO's
            detail page.
          </p>
        </div>
      ) : (
        <>
          <div className="list-toolbar">
            <FilterInput
              value={query}
              onChange={setQuery}
              placeholder="Search by ref, PO, receiver, or kind…"
              ariaLabel="Search receipts"
            />
            <Select
              value={kindFilter}
              onValueChange={(v) => setKindFilter(v as ReceiptKind | 'ALL')}
              aria-label="Kind filter"
              options={KIND_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
            />
            <span className="list-count t-body-sm">
              {visible.length} of {rows.length}
            </span>
          </div>

          {visible.length === 0 ? (
            <div className="empty-state">
              <p>No receipts match.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader sort={sort} columnKey="receivedAt" onSort={onSort}>
                    Received at
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="kind" onSort={onSort}>
                    Kind
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="poRef" onSort={onSort}>
                    PO
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="receivedBy" onSort={onSort}>
                    Received by
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="lines" onSort={onSort}>
                    Lines
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="attachments" onSort={onSort}>
                    Attachments
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr
                    key={r.receiptRef}
                    className="row-clickable"
                    onClick={() => navigate(`/receipts/${r.receiptRef}`)}
                  >
                    <td>{formatRelativeDate(r.receivedAt)}</td>
                    <td>
                      <Badge variant={r.kind === 'SERVICE' ? 'info' : 'success'} dot={false}>
                        {r.kind}
                      </Badge>
                    </td>
                    <td className="mono">{r.poRef.slice(0, 16)}…</td>
                    <td className="mono">{r.receivedBy}</td>
                    <td>{r.lines.length}</td>
                    <td>{r.attachments.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

interface SortHeaderProps {
  readonly sort: SortState<ReceiptSortKey> | null;
  readonly columnKey: ReceiptSortKey;
  readonly onSort: (key: ReceiptSortKey) => void;
  readonly children: JSX.Element | string;
}

function SortHeader({ sort, columnKey, onSort, children }: SortHeaderProps): JSX.Element {
  const active = sort?.key === columnKey;
  const indicator = active ? (sort?.direction === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th>
      <button
        type="button"
        className={`th-sort${active ? ' th-sort-active' : ''}`}
        onClick={() => onSort(columnKey)}
      >
        {children}
        <span className="th-sort-indicator" aria-hidden>
          {indicator}
        </span>
      </button>
    </th>
  );
}
